<script>
  import { afterUpdate, getContext } from "svelte";
  import TreeViewNode, { computeTreeLeafDepth } from "./TreeViewNode.svelte";
  import ActiveFolderIcon from "../svg/ActiveFolderIcon.svelte";
  import { sortedAccessRights } from "lib/stores.js";

  export let children = [];
  export let root = false;
  export let id = "";
  export let text = "";
  export let disabled = false;
  export let icon = undefined;
  export let type = "";
  export let parentid = "";
  export let access_right = {};
  export let parent_access_right = {};

  if (id) {
    const obj = { ...$sortedAccessRights };
    obj[id] = {
      parentid: parentid,
      access_right: access_right,
      parent_access_right: parent_access_right,
      text: text,
      type: type,
    };
    sortedAccessRights.set(obj);
  }

  let ref = null;
  let refLabel = null;
  let prevActiveId = undefined;
  let lineWidth = 0;

  const {
    activeNodeId,
    selectedNodeIds,
    expandedNodeIds,
    clickNode,
    selectNode,
    expandNode,
    focusNode,
    toggleNode,
    parentIds,
    onContextMenu,
  } = getContext("TreeView");
  const { onContextMenuChange } = getContext("TreeViewTest");

  const offset = () => {
    const depth = computeTreeLeafDepth(refLabel);

    if (parent) return depth;
    if (icon) return depth + 1;
    return depth + 1.5;
  };

  afterUpdate(() => {
    if (id === $activeNodeId && prevActiveId !== $activeNodeId) {
      if (!$selectedNodeIds.includes(id)) selectNode(node);
    }

    prevActiveId = $activeNodeId;
  });

  function showContextMenu(event) {
    event.preventDefault();
    // if (type !== "folder") return;
    onContextMenu({ node, event, type });
    onContextMenuChange({ node, event, type });
  }

  $: parent = Array.isArray(children);
  $: node = { id, text, expanded, leaf: !parent };
  $: if (refLabel) {
    refLabel.style.marginLeft = `-${offset()}rem`;
    // refLabel.style.paddingLeft = `${offset()}rem`;
    lineWidth = offset();
  }
  $: expanded = $expandedNodeIds.includes(id);
</script>

{#if root}
  {#each children as child (child.id)}
    {#if Array.isArray(child.children)}
      <svelte:self
        parentid={id}
        parent_access_right={access_right}
        {sortedAccessRights}
        {...child}
      />
    {:else}
      <TreeViewNode leaf {...child} />
    {/if}
  {/each}
{:else}
  <li
    bind:this={ref}
    role="treeitem"
    {id}
    class="list"
    tabindex={disabled ? undefined : -1}
    aria-current={id === $activeNodeId || undefined}
    aria-selected={disabled ? undefined : $selectedNodeIds.includes(id)}
    aria-disabled={disabled}
    class:bx--tree-node={true}
    class:bx--tree-parent-node={true}
    class:bx--tree-node--active={id === $activeNodeId}
    class:bx--tree-node--selected={$selectedNodeIds.includes(id)}
    class:bx--tree-node--disabled={disabled}
    class:bx--tree-node--with-icon={icon}
    aria-expanded={expanded}
    on:click|stopPropagation={() => {
      if (disabled) return;
      clickNode(node);
    }}
    on:keydown={(e) => {
      if (
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "Enter"
      ) {
        e.stopPropagation();
      }

      if (parent && e.key === "ArrowLeft") {
        expanded = false;
        expandNode(node, false);
        toggleNode(node);
      }

      if (parent && e.key === "ArrowRight") {
        if (expanded) {
          ref.lastChild.firstElementChild?.focus();
        } else {
          expanded = true;
          expandNode(node, true);
          toggleNode(node);
        }
      }

      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (disabled) return;
        expanded = !expanded;
        toggleNode(node);
        clickNode(node);
        expandNode(node, expanded);
        ref.focus();
      }
    }}
    on:focus={() => {
      focusNode(node);
    }}
  >
    <div class:bx--tree-node__label={true} bind:this={refLabel}>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      {#if lineWidth}
        <div class="line" style={`width:${lineWidth + 1}rem;`} />
      {/if}
      <button
        on:contextmenu={showContextMenu}
        {id}
        class:bx--tree-parent-node__toggle={true}
        {disabled}
        on:click={() => {
          if (disabled || type !== "folder") return;
          expanded = !expanded;
          expandNode(node, expanded);
          toggleNode(node);
        }}
        class="label-btn {$activeNodeId === id ? 'bx-selected' : ''}"
      >
        <span
          {id}
          class:bx--tree-node__label__details={true}
          class:active-bx-tree-node={$parentIds.includes(id)}
        >
          <svelte:component
            this={expanded && type === "folder" ? ActiveFolderIcon : icon}
            class="bx--tree-node__icon {$activeNodeId === id
              ? 'selected-node'
              : ''}"
          />
          <span
            style="overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          width: 190px;
          display: inline-block;
          text-align:justify"
          >
            {text}
          </span>
        </span>
      </button>
    </div>
    {#if expanded}
      <ul role="group" class:bx--tree-node__children={true}>
        {#each children as child (child.id)}
          {#if Array.isArray(child.children)}
            <svelte:self
              parentid={id}
              parent_access_right={access_right}
              {sortedAccessRights}
              {...child}
            />
          {:else}
            <TreeViewNode leaf {...child} />
          {/if}
        {/each}
      </ul>
    {/if}
  </li>
{/if}

<style>
  .label-btn {
    display: flex;
    width: 100%;
    /* justify-content: space-between; */
    align-items: center;
  }
  .line {
    height: 1px;
    background: rgba(40, 40, 40, 0.25);
    margin-right: 10px;
  }
  :global(.bx--tree-node__label::before) {
    display: none;
  }

  :global(.bx--tree .bx--tree-node__label) {
    padding: 0 0.5rem;
  }

  :global(.bx--tree .bx--tree-node--selected > .bx--tree-node__label) {
    background: transparent !important;
  }

  :global(.bx--tree .bx--tree-node__label:hover) {
    background: transparent !important;
  }

  .active-bx-tree-node {
    font-weight: 700 !important;
  }

  :global(.plus-icon) {
    opacity: 50%;
  }

  :global(.selected-node > path) {
    fill: #0095ff;
  }

  :global(.bx-selected) {
    color: #0095ff;
  }
</style>
